import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser, useSession } from "@clerk/clerk-react";
import LoadingModal from "./Skeletons/LoadingModal";
import { shouldDeferMfaSetupRedirectForSession } from "../utils/mfaSetupSessionDeferral";
import {
  isOrgMfaEnabled,
  isPublicMetadataTwoFactorEnabled,
  isClerkTotpEnrolled,
} from "../utils/mfaClerkPublicMetadata";

/**
 * Enforces MFA policy before role/rights-gated routes. Uses only Clerk `user` +
 * `publicMetadata` (org_mfa_enabled, two_factor_enabled) — no MfaStatus GET.
 *
 * - org MFA && no Clerk TOTP -> /mfa/setup (no Skip)
 * - optional: metadata two_factor_enabled && no TOTP -> /mfa/setup?optional=true
 *   (unless profile opt-in deferred for this Clerk session — mfaSetupSessionDeferral)
 * - Skip on optional wizard should PATCH two_factor_enabled false so redirect stops.
 */
export function MfaEnforcedRoute({ children, variant }) {
  void variant; // kept for call-site consistency; MFA policy is read from Clerk user only
  const location = useLocation();
  const { user, isLoaded: userLoaded } = useUser();
  const { session } = useSession();

  const path = location.pathname;
  if (path === "/mfa/setup" || path === "/terms") {
    return children;
  }

  if (!userLoaded) {
    return <LoadingModal open={true} />;
  }

  const orgReq = isOrgMfaEnabled(user);
  const totp = isClerkTotpEnrolled(user);
  const metaWantsMfa = isPublicMetadataTwoFactorEnabled(user);

  if (orgReq && !totp) {
    return <Navigate to="/mfa/setup" replace />;
  }

  if (!orgReq && !totp && metaWantsMfa) {
    if (shouldDeferMfaSetupRedirectForSession(session?.id)) {
      return children;
    }
    return <Navigate to="/mfa/setup?optional=true" replace />;
  }

  return children;
}
